import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Trash2, Pencil, Mail, MessageSquare, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Panel, Empty } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { useFeature } from "@/context/AuthContext";

const CH = [["email", "Email", Mail], ["sms", "SMS", MessageSquare], ["whatsapp", "WhatsApp", MessageCircle]];
const EMPTY = { name: "", channel: "email", subject: "", body: "" };

export const MessageTemplates = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState("");
  const [ai, setAi] = useState(false);
  const aiOn = useFeature("ai");
  const load = () => api.get("/message-templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const draft = async () => {
    if (goal.trim().length < 3) return toast.error("Descrivi in poche parole l'obiettivo del messaggio");
    setAi(true);
    try { const r = await api.post("/message-templates/draft-ai", { goal, channel: f.channel }); setF({ ...f, name: f.name || r.data.name, subject: r.data.subject, body: r.data.body }); toast.success("Bozza generata: rivedila e salva"); }
    catch (e) { toast.error(errMsg(e)); } finally { setAi(false); }
  };

  const save = async () => {
    if (!f.name.trim() || !f.body.trim()) return toast.error("Inserisci nome e testo del modello");
    setBusy(true);
    try {
      if (editId) await api.put(`/message-templates/${editId}`, f); else await api.post("/message-templates", f);
      toast.success(editId ? "Modello aggiornato" : "Modello salvato"); setF(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (t) => { if (!window.confirm(`Eliminare il modello "${t.name}"?`)) return; await api.delete(`/message-templates/${t.id}`); load(); };
  const edit = (t) => { setEditId(t.id); setF({ name: t.name, channel: t.channel, subject: t.subject || "", body: t.body }); };

  return (
    <Panel title="Modelli messaggio (scheda cliente)" testId="message-templates">
      <div className="grid gap-5 p-5 lg:grid-cols-[1fr_1fr]">
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Testi riutilizzabili per i messaggi singoli dalla scheda cliente. Variabili: {"{nome} {cognome} {nome_completo} {punti} {livello} {carta} {citta} {email} {telefono}"}</p>
          <Input data-testid="tpl-name-input" placeholder="Nome modello (es. Saldo punti)" value={f.name} onChange={set("name")} />
          <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
            {CH.map(([k, l, Icon]) => <button key={k} data-testid={`tpl-channel-${k}`} onClick={() => setF({ ...f, channel: k })} className={`flex items-center justify-center gap-2 rounded-md py-1.5 text-sm ${f.channel === k ? "bg-white shadow-sm" : "text-slate-500"}`}><Icon className="h-4 w-4" />{l}</button>)}
          </div>
          <div className="flex gap-2">
            {aiOn && <><Input data-testid="tpl-goal-input" placeholder="Obiettivo per l'IA (es. avvisare che i punti scadono a fine mese)" value={goal} onChange={(e) => setGoal(e.target.value)} />
            <Button variant="outline" data-testid="tpl-ai-button" onClick={draft} disabled={ai || busy}><Sparkles className="mr-2 h-4 w-4" />{ai ? "Scrivo…" : "Scrivi con IA"}</Button></>}
          </div>
          {f.channel === "email" && <Input data-testid="tpl-subject-input" placeholder="Oggetto" value={f.subject} onChange={set("subject")} />}
          <Textarea data-testid="tpl-body-input" rows={5} placeholder="Gentile {nome}, ..." value={f.body} onChange={set("body")} />
          <div className="flex gap-2">
            <Button data-testid="tpl-save-button" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700"><BookmarkPlus className="mr-2 h-4 w-4" />{editId ? "Aggiorna modello" : "Salva modello"}</Button>
            {editId && <Button variant="ghost" data-testid="tpl-cancel-button" onClick={() => { setEditId(null); setF(EMPTY); }}>Annulla</Button>}
          </div>
        </div>
        <div className="max-h-96 overflow-auto rounded-lg border border-slate-200" data-testid="tpl-list">
          {list.length === 0 ? <Empty text="Nessun modello salvato." /> : list.map((t) => {
            const Icon = CH.find(([k]) => k === t.channel)[2];
            return (
              <div key={t.id} className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`tpl-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-800"><Icon className="h-3.5 w-3.5 text-slate-400" />{t.name}</div>
                  {t.subject && <div className="text-xs text-slate-600">{t.subject}</div>}
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{t.body}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="outline" data-testid={`tpl-edit-${t.id}`} onClick={() => edit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`tpl-delete-${t.id}`} onClick={() => del(t)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
};
